import { inject, Injectable } from '@angular/core';
import { ApiService, Order, OrderState, PaymentType, Table } from '@us/web/shared/data-access';
import { map, Observable } from 'rxjs';
import { parseDataServer } from '@us/web/shared/utils';
import { injectQuery } from '@ngneat/query';
import { omit } from 'lodash';
import { GetOrderParams } from '../models';

@Injectable({
    providedIn: 'root'
})
export class OrdersApiService {
    private readonly url = 'orders';
    private readonly pushOrderStateUrl = 'order/state';
    private readonly estimateOrderFeeUrl = 'order/estimated-fee';
    private readonly transactionStatusOrderUrl = 'order/transaction-status';
    private readonly callWaiterUrl = 'call-waiter';
    private readonly pushNotificationUrl = 'order/push-notification';

    private readonly apiService = inject(ApiService);
    private readonly query = injectQuery();

    getOrders(): Observable<Order[]> {
        return this.apiService.get(this.url).pipe(
            map(response => {
                return response.map((e: any) => parseDataServer(e));
            })
        );
    }

    getOrder(params: GetOrderParams): Observable<Order> {
        return this.apiService
            .get(`orders/${params.id}`, { params: omit(params, 'id') })
            .pipe(map(response => parseDataServer(response)));
    }

    getOrderQuery(params: GetOrderParams) {
        return this.query({
            queryKey: [`order-detail`, params],
            queryFn: () => this.getOrder(params)
        });
    }

    pushOrderState(params: { order_id: string; state: OrderState; message?: string }): Observable<Order> {
        return this.apiService.put(this.pushOrderStateUrl, params).pipe(map(response => parseDataServer(response)));
    }

    getTransactionStatus(id: string) {
        return this.apiService
            .get(this.transactionStatusOrderUrl, { params: { id } })
            .pipe(map(response => parseDataServer(response)));
    }

    callWaiter(table: Table) {
        const params = {
            table_id: table.id,
            table_name: table.name,
            area_id: table.area_id,
            area_name: table.area_name
        };
        return this.apiService.post(this.callWaiterUrl, params);
    }

    postRating(params: { rating: number; description: string; order_id: string; account_id: number }) {
        return this.apiService.post('rating', params);
    }

    validateTable(params: { tableId: number; shopId?: string }) {
        return this.apiService.post<Table>(`validate-table`, params);
    }

    updatePaymentMethod(orderId: string, paymentType: PaymentType, payment_detail?: any) {
        let params: {
            payment_type: PaymentType;
            payment_detail?: any;
        } = { payment_type: paymentType };

        if (payment_detail) {
            params.payment_detail = payment_detail;
        }
        return this.apiService
            .patch(`orders/${orderId}/payment`, params)
            .pipe(map(response => parseDataServer(response)));
    }
}
